import React, { useEffect } from 'react'
import WhyChooseUs from '../Components/Home/WhyChooseUs'
import { useSEO } from './Seo'
import FirstSection from '../Components/Home/FirstSection'
import HeroSection from '../Components/Home/HeroSection'
import FeaturedSection from '../Components/Home/FeaturedSection'
import GameGallery from '../Components/Home/GameGallary'
import { Helmet } from 'react-helmet-async' 
import BlogList from '../Components/BlogList'

const Home = () => {

const STORAGE_KEY = "gameListPage"

useSEO({
  title: "Play Free Online Games | Best HTML5 Games at Game Zone Hub",
  description: "Play 1000+ free online games instantly. Discover action, racing, puzzle, and multiplayer games. No downloads or sign-ups required. Play now at Game Zone Hub!",
  keywords: "play free online games, free online games, html5 games, browser games, unblocked games, play games online free, Game Zone Hub",
  canonical: "https://www.gamezonehub.in/",
  ogTitle: "Play Free Online Games Instantly | Game Zone Hub",
  ogDescription: "Play the best free online games directly in your browser. Action, puzzle, racing, and more. No downloads, just play free online!",
  ogImage: "https://www.gamezonehub.in/og-image.webp",
  twitterCard: "summary_large_image",
});

const schemaData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebSite",
      "name": "Game Zone Hub",
      "url": "https://www.gamezonehub.in/",
      "description": "Play free online games instantly. The ultimate hub for HTML5 browser games without any downloads.",
      "potentialAction": {
        "@type": "SearchAction",
        "target": "https://www.gamezonehub.in/search?q={search_term_string}",
        "query-input": "required name=search_term_string"
      }
    },
    {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Are the games on Game Zone Hub completely free?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes! All the games you see on our site are totally free to play. You don't need to pay any money or subscribe to anything to enjoy them."
          }
        },
        {
          "@type": "Question",
          "name": "Do I need to download games to play?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "No downloads at all. You can play every game right here in your web browser. This saves your phone or computer storage from getting full."
          }
        },
        {
          "@type": "Question",
          "name": "Can I play on my mobile phone?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Definitely. Most of our games are built to work perfectly on mobile screens, tablets, and desktop computers."
          }
        },
        {
          "@type": "Question",
          "name": "Do I need to create an account to play?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "No, you don't need to sign up or create an account. Just click on the game you like and start playing immediately."
          }
        }
      ]
    }
  ]
};

useEffect(()=>{
  localStorage.setItem(STORAGE_KEY, String(1))
  window.scrollTo({top:"0"})
},[])

  return (
    <div className='bg-gradient-to-br from-gray-900 via-black to-gray-800 min-h-screen pt-[80px] '>
         <Helmet>
           {schemaData && (
             <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
           )}
         </Helmet>

         <FirstSection/>
         <HeroSection/>
         <FeaturedSection/>
         <WhyChooseUs/>
         <GameGallery/>
         <BlogList/>

         {/* 🚀 NATURAL HUMAN WRITTEN SEO CONTENT BLOCK FOR ADSENSE APPROVAL */}
         <section className="bg-[#0b1120] border-t border-white/5 sm:py-16 py-5  text-gray-300" data-aos="fade-up">
           <div className="container px-4 mx-auto">
             
             {/* Main SEO Heading */}
             <div className="text-center sm:mb-16 mb-10" data-aos="zoom-in">
                <h1 className="text-2xl sm:text-3xl md:text-4xl xl:text-5xl sm:mt-0 mt-2 font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300 mb-6 ">
                  Your Home for Free Online Games
                </h1>
                <p className="text-gray-400 max-w-4xl mx-auto text-lg leading-relaxed">
                  Welcome to Game Zone Hub! If you're looking for a quick and fun way to pass the time, you've found the right spot. We have put together an awesome collection of browser games that you can play without paying a dime or downloading huge files. Just browse, click, and play.
                </p>
             </div>

             {/* Detailed Overview Section */}
             <div className="grid lg:grid-cols-2 sm:gap-12 gap-6 sm:mb-16 mb-10">
               <div className="space-y-6">
                 <h2 className="text-3xl font-extrabold text-white">Why Play Browser Games?</h2>
                 <div className="w-20 h-1 bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full"></div>
                 <div className="leading-relaxed space-y-4 text-sm sm:text-base">
                   <p>
                     A few years ago, playing games meant spending hours downloading large files and worrying if your computer was fast enough. Now, things are much easier. Thanks to HTML5, we can offer games that run smoothly straight from your Chrome, Safari, or Firefox browser. 
                   </p>
                   <p>
                     We spend a lot of time testing and picking the best games so you don't have to deal with lag or annoying bugs. It's just simple, straightforward fun.
                   </p>
                 </div>
               </div>
               
               <div className="space-y-6">
                 <h2 className="text-3xl font-extrabold text-white">Play Anywhere, Anytime</h2>
                 <div className="w-20 h-1 bg-gradient-to-r from-purple-500 to-pink-400 rounded-full"></div>
                 <div className="leading-relaxed space-y-4 text-sm sm:text-base">
                   <p>
                     Whether you are relaxing at home on your PC or taking a bus ride with your smartphone, Game Zone Hub is always ready. Our website adjusts automatically to fit whatever device you are using.
                   </p>
                   <p>
                     You can easily use your keyboard and mouse for intense action games, or use simple touch controls when you are playing puzzles on your phone. The fun doesn't stop when you step away from your desk.
                   </p>
                 </div>
               </div>
             </div>

             {/* Categories Section */}
             <div className="mb-16">
                 <h2 className="text-3xl font-extrabold text-center text-white mb-4">Find Your Favorite Genre</h2>
                 <p className="text-center text-gray-400 mb-10 max-w-2xl mx-auto">We have sorted our games into easy categories so you can quickly find exactly what you feel like playing today.</p>
                 
                 <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                   <div className="bg-[#1e293b] p-6 rounded-2xl border border-white/5 hover:border-blue-500/30 transition-all shadow-lg hover:-translate-y-1">
                     <h3 className="font-bold text-xl text-blue-400 mb-3 flex items-center gap-2">🏎️ Car & Racing</h3>
                     <p className="text-sm leading-relaxed text-gray-400">If you love speed, check out our racing section. Drive fast cars on city streets, race dirt bikes over hills, or try out extreme off-road driving.</p>
                   </div>
                   <div className="bg-[#1e293b] p-6 rounded-2xl border border-white/5 hover:border-purple-500/30 transition-all shadow-lg hover:-translate-y-1">
                     <h3 className="font-bold text-xl text-purple-400 mb-3 flex items-center gap-2">🧠 Puzzle & Logic</h3>
                     <p className="text-sm leading-relaxed text-gray-400">Give your brain a workout with tricky puzzles. We have everything from matching games and Sudoku to physics-based brain teasers that will keep you thinking.</p>
                   </div>
                   <div className="bg-[#1e293b] p-6 rounded-2xl border border-white/5 hover:border-red-500/30 transition-all shadow-lg hover:-translate-y-1">
                     <h3 className="font-bold text-xl text-red-400 mb-3 flex items-center gap-2">⚔️ Action & Fighting</h3>
                     <p className="text-sm leading-relaxed text-gray-400">Jump right into the action. Defend your base, fight off waves of enemies, or go head-to-head in fun stickman brawls. Fast reflexes are required!</p>
                   </div>
                   <div className="bg-[#1e293b] p-6 rounded-2xl border border-white/5 hover:border-green-500/30 transition-all shadow-lg hover:-translate-y-1">
                     <h3 className="font-bold text-xl text-green-400 mb-3 flex items-center gap-2">🌐 Multiplayer IO</h3>
                     <p className="text-sm leading-relaxed text-gray-400">Play against real people from around the world. Grow the biggest snake, survive the longest, and try to reach the top of the live leaderboards.</p>
                   </div>
                   <div className="bg-[#1e293b] p-6 rounded-2xl border border-white/5 hover:border-pink-500/30 transition-all shadow-lg hover:-translate-y-1">
                     <h3 className="font-bold text-xl text-pink-400 mb-3 flex items-center gap-2">🍳 Casual & Relaxing</h3>
                     <p className="text-sm leading-relaxed text-gray-400">Just want to chill? Try running a virtual restaurant, playing simple pop-it games, or managing a farm. Perfect for a quick, stress-free break.</p>
                   </div>
                   <div className="bg-[#1e293b] p-6 rounded-2xl border border-white/5 hover:border-yellow-500/30 transition-all shadow-lg hover:-translate-y-1">
                     <h3 className="font-bold text-xl text-yellow-400 mb-3 flex items-center gap-2">🛡️ Unblocked Fun</h3>
                     <p className="text-sm leading-relaxed text-gray-400">We make sure our site connects securely, so you can enjoy these unblocked games easily during your free time without dealing with heavy restrictions.</p>
                   </div>
                 </div>
             </div>

             {/* SEO FAQ SECTION FOR RICH SNIPPETS */}
             <div className="bg-gradient-to-br from-[#1e293b] to-black/60 rounded-3xl sm:p-8 p-6 sm:p-12 border border-white/10 shadow-2xl mb-12" data-aos="fade-up">
                <h2 className="text-3xl font-extrabold text-center text-white mb-10">Common Questions</h2>
                <div className="grid md:grid-cols-2 gap-8">
                    <div>
                        <h3 className="font-bold text-lg text-cyan-400 mb-2">Are these games actually free?</h3>
                        <p className="text-gray-400 leading-relaxed text-sm sm:text-base">Yes! All the games you see on our site are totally free to play. You don't need to pay any money or subscribe to anything to enjoy them.</p>
                    </div>
                    <div>
                        <h3 className="font-bold text-lg text-cyan-400 mb-2">Do I need to download anything?</h3>
                        <p className="text-gray-400 leading-relaxed text-sm sm:text-base">No downloads at all. You can play every game right here in your web browser. This saves your phone or computer storage from getting full.</p>
                    </div>
                    <div>
                        <h3 className="font-bold text-lg text-cyan-400 mb-2">Can I play on my mobile phone?</h3>
                        <p className="text-gray-400 leading-relaxed text-sm sm:text-base">Definitely. Most of our games are built to work perfectly on mobile screens, tablets, and desktop computers.</p>
                    </div>
                    <div>
                        <h3 className="font-bold text-lg text-cyan-400 mb-2">Do I need to create an account?</h3>
                        <p className="text-gray-400 leading-relaxed text-sm sm:text-base">No, you don't need to sign up or create an account. Just click on the game you like and start playing immediately.</p>
                    </div>
                </div>
             </div>
             
             <div className="text-center bg-gradient-to-r from-blue-900/40 to-cyan-900/40 sm:p-10 p-6 rounded-3xl border border-blue-500/20 shadow-[0_0_30px_rgba(59,130,246,0.1)]">
               <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">Start Your Game Now!</h3>
               <p className="text-gray-300 mb-0 max-w-3xl mx-auto text-sm sm:text-base leading-relaxed">
                 Join thousands of people who visit Game Zone Hub every day for their gaming fix. Browse the categories above, find something you like, and hit play. Have fun!
               </p>
             </div>
             
           </div>
         </section>

    </div>
  )
}

export default Home